import { loadDBFromSupabase, saveDBToSupabase } from './supabase'

const DB_KEY = 'novacms_db'

export interface CheckoutFormData {
  fullName: string
  email: string
  address: string
  city: string
  phone: string
}

export interface CheckoutCartItem {
  id: string | number
  name: string
  price: number
  quantity: number
}

/**
 * Saves a real order (+ the customer as a contact) into the same database the
 * CMS admin panel reads from — same shape as orders created inside the CMS
 * itself, so it shows up in Orders there immediately.
 *
 * Reads the freshest copy of the DB first (Supabase, falling back to the
 * local cache), appends the new contact + order, then writes back to both —
 * exactly the same pattern CMS.tsx's own save() uses, so this is safe to run
 * from the storefront without the CMS being open.
 */
export async function createOrderFromCheckout(
  form: CheckoutFormData,
  items: CheckoutCartItem[],
  shippingCost: number
): Promise<{ ok: boolean; orderNum?: number; message?: string }> {
  try {
    // 1. Get the freshest DB we can find.
    let db: Record<string, unknown> | null = await loadDBFromSupabase()
    if (!db) {
      const local = localStorage.getItem(DB_KEY)
      db = local ? JSON.parse(local) : null
    }
    if (!db) {
      return { ok: false, message: 'Baza podataka trenutno nije dostupna. Pokušajte ponovo za koji trenutak.' }
    }

    const contacts = Array.isArray(db.contacts) ? [...(db.contacts as Record<string, unknown>[])] : []
    const orders = Array.isArray(db.orders) ? [...(db.orders as Record<string, unknown>[])] : []

    // 2. Reuse an existing contact by email if we already have one, else create a new one.
    const [fname, ...rest] = form.fullName.trim().split(' ')
    const lname = rest.join(' ')
    let contact = contacts.find((c) => (c.email as string)?.toLowerCase() === form.email.trim().toLowerCase())
    if (!contact) {
      contact = {
        id: `contact-${Date.now()}-${Math.floor(Math.random() * 999)}`,
        fname: fname || form.fullName,
        lname: lname || '',
        email: form.email.trim(),
        phone: form.phone.trim(),
        company: '',
        value: 0,
        stage: 'customer',
        source: 'Web prodavnica',
        notes: `Adresa: ${form.address}, ${form.city}`,
        createdAt: Date.now(),
      }
      contacts.push(contact)
    }

    const subtotal = items.reduce((sum, it) => sum + it.price * it.quantity, 0)
    const total = subtotal + shippingCost
    const orderNum = orders.length ? Math.max(...orders.map((o) => Number(o.num) || 0)) + 1 : 1001

    const order = {
      id: `order-${Date.now()}-${Math.floor(Math.random() * 999)}`,
      num: orderNum,
      contactId: contact.id,
      items: items.map((it) => ({
        prodId: String(it.id),
        name: it.name,
        price: it.price,
        qty: it.quantity,
        variation: '',
      })),
      status: 'new',
      payment: 'Pouzećem',
      payStatus: 'unpaid',
      shipping: `${form.address}, ${form.city}`,
      notes: '',
      discount: 0,
      subtotal,
      total,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    }
    orders.push(order)

    const newDb = { ...db, contacts, orders }

    // 3. Write back to both caches, same as the CMS does.
    try {
      localStorage.setItem(DB_KEY, JSON.stringify(newDb))
      window.dispatchEvent(new StorageEvent('storage', { key: DB_KEY, newValue: JSON.stringify(newDb) }))
    } catch {
      // local cache full — not fatal, the Supabase write below is what matters
    }
    const result = await saveDBToSupabase(newDb)
    if (!result.ok) {
      return { ok: false, message: `Porudžbina je zabeležena lokalno, ali nije stigla do baze: ${result.message}` }
    }

    return { ok: true, orderNum }
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : 'Nepoznata greška prilikom čuvanja porudžbine.' }
  }
}
